import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { useStabileListe } from "../components/stabileListe.js";
import type { ApiClient, Angebot, Unternehmen } from "@kundeninfo/api-client";
import { Modal, ModalFuss, darfAbbrechen } from "@kundeninfo/ui";
import { Auswahl, Hinweis } from "@kundeninfo/ui/me-design";
import { AngebotDetail, angebotFormularFelder, angebotVorschauWerte,
         useAngebotsUmfeld } from "../components/AngebotDetail.js";
import type { AngebotDetailClient } from "../components/AngebotDetail.js";
import { useAngebotAnlegen } from "../components/angebotAnlegen.js";
import { apiMeldung, entwuerfeGleich } from "../components/vorgangsdetail.js";
import { KiKnopf } from "../components/meHuellen.js";
import { HistoricalRecordPanel } from "../components/historical/HistoricalRecordPanel.js";
import { HISTORICAL_TEXT } from "../components/historical/texts.js";
import type { AkteSprung } from "./KundenAkte.js";
import { jumpNote } from "./customerOverview/targets.js";
import "./KundeAngebote.css";

/** Nur die Klientenmethoden, die dieser Reiter wirklich braucht — so lässt er sich im Test
 *  mit einer Handvoll vi.fn zeichnen (Muster aus KundeAnsprechpartner.tsx / KundeNotizen.tsx).
 *  Der grössere Teil davon gehört seit dem Bausteinschnitt (Bereichs-Spalten, Etappe 1,
 *  Aufgabe 2) dem Detail (`AngebotDetailClient`); der Reiter selbst legt nur noch die LISTE
 *  und das Anlegen dazu. */
export type AngeboteClient = AngebotDetailClient
  & Pick<ApiClient, "listOffers" | "createOffer">;

/** Die Verdrahtung, die von App über KundenAkte hierher durchgereicht wird (erst in T9). */
export type AngeboteUmfeld = {
  client: AngeboteClient;
  /** Die Unternehmen, zu denen der Kunde eine Beziehung hat — die EINZIGEN, die beim Anlegen
   *  wählbar sind (Ruling: „nur Unternehmen mit Kundenbeziehung"). Der Elternteil KundenAkte
   *  filtert sie in T9 aus `kunde.relations`; ein Angebot verweist immer auf eines von ihnen,
   *  sodass hier auch die Nummer/Kürzel-Auflösung bestehender Angebote greift. */
  companies: Unternehmen[];
  /** Der Callback der Angebot→Auftrag-Naht (Ruling P3A-B): am ACCEPTED-Angebot ruft „Auftrag
   *  anlegen" ihn mit den Angebotsdaten — der Reiter erzeugt selbst KEINEN Auftrag. */
  onAuftragAusAngebot: (angebot: Angebot) => void;
  /** Das Angebot, das ein Sprung aufschlagen soll: Der Reiter öffnet es, sobald seine Liste steht, statt den
   *  Bearbeiter den eben angeklickten Vorgang ein zweites Mal suchen zu lassen. Muster und Begründung wie bei
   *  `oeffneRechnungId` am Rechnungen-Reiter. Der einzige Sprung mit Vorgang kommt heute aus der Kundenübersicht; die
   *  globale Angebotsliste ist die Objektliste ihres Bereichs (Etappe 1, Aufgabe 3). */
  oeffneAngebotId?: string | null;
  /** Woher der Sprung kommt: Nur der Sprung aus der Kundenübersicht sagt es so; fehlt die Angabe, gilt die Suche
   *  (Bestandsverhalten, der Satz „Aus der Angebotssuche geöffnet.“). */
  jumpSource?: AkteSprung["source"];
  /** Der Sprung „Ordner“ aus dem Abschnitt „Dateien“ eines Vorgangs in den Reiter „Dateien“ (Akte). */
  onOrdnerOeffnen: (folderId: string) => void;
};

export type KundeAngeboteProps = AngeboteUmfeld & {
  kundeId: string;
  /** Die Sicht der Akte (Mandantentrennung E359) — `undefined` = das aktive Unternehmen. */
  companyIds?: readonly string[] | undefined;
  /** Das Arbeitsunternehmen aus der Kopfleiste (ME-Etappe 1, Aufgabe 7) — Vorgabe des
   *  Unternehmens beim Anlegen, wenn der Kunde dort eine Beziehung hat. `null`: Bestand. */
  arbeitsunternehmenId: string | null;
  /** Der Firmenname des Kunden — allein für die Platzhalter-Vorschau (`#{kunde.name}`).
   *  Weglassbar: Fehlt er, bleibt das Token in der Vorschau sichtbar stehen, und der Reiter
   *  bleibt in jedem bestehenden Test unverändert bedienbar. */
  kundeName?: string;
};

/**
 * Der Angebote-Reiter: Liste (neueste oben), Anlegen mit companyId und der Sprung aus der
 * globalen Suche. Das DETAIL eines Angebots — Kopfzeile, Statusaktionen, Bearbeiten samt
 * Positionen und Vorlagen, „Als PDF" und die Angebot→Auftrag-Naht — zeichnet seit dem
 * Bausteinschnitt (Bereichs-Spalten, Etappe 1, Aufgabe 2) `components/AngebotDetail.tsx`.
 * Denselben Baustein zeichnet ab Aufgabe 3 der Angebots-BEREICH: Der Reiter behält damit
 * kein eigenes, zweites Detail, das mit dem dritten Fix auseinanderliefe.
 *
 * Beim Reiter bleibt, was zur ANSICHT gehört und nicht zum einzelnen Vorgang: die Liste,
 * das Anlegen, der Meldungsstreifen und die Entscheidung, WELCHER Vorgang gerade bearbeitet
 * wird (genau einer — daran hängt `formularOffen`).
 *
 * Eigene Wurzelklasse `.kang` (NICHT `.ka` teilen — Kollisions-Lehre 01.09., siehe die
 * Notiz in KundeNotizen.tsx / KundeAnlegen).
 */
export function KundeAngebote(p: KundeAngeboteProps): ReactElement {
  const { client, kundeId, companies, onAuftragAusAngebot } = p;

  const [angebote, setAngebote] = useState<Angebot[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);

  const [neuOffen, setNeuOffen] = useState(false);
  /** Das Panel „Historisches Angebot erfassen“ (Aufgabe 26) — ebenfalls eines der Formulare dieser Ansicht. */
  const [historischOffen, setHistorischOffen] = useState(false);
  /** Die Kennung des gerade bearbeiteten Vorgangs — genau einer je Ansicht. Der Baustein
   *  bekommt sie als `bearbeitung` gereicht; die Version zum Speichern liest er aus dem
   *  Satz, den diese Liste führt. */
  const [bearbeitetId, setBearbeitetId] = useState<string | null>(null);

  /** Lädt die Liste und GIBT sie zurück: Das Anlegen braucht den frischen Stand, um das
   *  gerade angelegte Angebot gleich zum Bearbeiten zu öffnen (siehe useAngebotAnlegen). */
  const sicht = useStabileListe(p.companyIds);
  const laden = useCallback(async (): Promise<Angebot[]> => {
    try {
      const { items } = await client.listOffers(kundeId, sicht);
      setAngebote(items);
      setFehler(null);
      return items;
    } catch (e) {
      setFehler(apiMeldung(e, "Die Angebote konnten nicht geladen werden."));
      return [];
    }
  }, [client, kundeId, sicht]);

  /** Das ANLEGEN führt der gemeinsame Hook (components/angebotAnlegen.ts) — derselbe, den
   *  der Angebots-Bereich in Aufgabe 3 benutzt. Hier bleibt nur, WOHIN sein Ergebnis
   *  fällt. */
  /** Belege entstehen im aktiven Unternehmen (Spec Mandantentrennung, E368): genau
   *  dieses ist wählbar — und damit vorgewählt; das Feld „Unternehmen" entfällt. */
  const anlegenUnternehmen = companies.filter((c) => c.id === p.arbeitsunternehmenId);
  const anlage = useAngebotAnlegen({
    client, kundeId, companies: anlegenUnternehmen, feldPraefix: "kang-neu",
    arbeitsunternehmenId: p.arbeitsunternehmenId,
    onSchliessen: () => { schliessen(); },
    onNeuLaden: laden,
    onBearbeiten: (a, meldung) => { bearbeitenOeffnen(a, meldung); },
  });
  const { entwurf, setEntwurf } = anlage;

  useEffect(() => { void laden(); }, [laden]);

  const bearbeitet = angebote?.find((a) => a.id === bearbeitetId) ?? null;
  const formularOffen = neuOffen || historischOffen || bearbeitetId !== null;

  /**
   * Das Unternehmen, dessen Vorlagen gerade gebraucht werden: beim Anlegen das im Formular
   * gewählte, beim Bearbeiten das des Angebots. Eine Vorlage gehört immer genau einer Firma
   * — eine gemeinsame Liste über alle wäre eine Auswahl, aus der die Hälfte 422 ergäbe.
   */
  const vorlagenFirma = neuOffen ? entwurf.companyId : (bearbeitet?.companyId ?? "");

  /** Die Auswahllisten lädt der REITER, einmal für alle Zeilen (siehe useAngebotsUmfeld) —
   *  und reicht sie an jedes Detail durch. */
  const umfeld = useAngebotsUmfeld(client, kundeId, vorlagenFirma);

  function schliessen(): void {
    setNeuOffen(false);
    setBearbeitetId(null);
    anlage.zuruecksetzen();
  }

  /**
   * Der EINE Ausgang der Anlage neben dem Anlegen (Hausregel „striktes Modalpanel",
   * 06.09.2026): kein Ausgang unter laufendem Schreiben, Rückfrage, sobald im leeren
   * Formular etwas steht. Die Vergleichsgrundlage führt der Anlege-Hook selbst
   * (`anlage.ausgang`) — nur dort steht, wie ein frischer Entwurf aussieht.
   */
  async function anlageAbbrechen(): Promise<void> {
    if (anlage.speichert) return;
    if (!(await darfAbbrechen(!entwuerfeGleich(entwurf, anlage.ausgang)))) return;
    schliessen();
  }

  function neuOeffnen(): void {
    anlage.zuruecksetzen();
    setHinweis(null);
    setBearbeitetId(null);
    setNeuOffen(true);
  }

  /**
   * Der Sprung aus der Kundenübersicht: Das dort angeklickte Angebot wird aufgeschlagen,
   * sobald die Liste des Reiters steht.
   *
   * Genau EINMAL (`sprungErledigt`) — sonst risse ein späteres Neuladen der Liste dem
   * Bearbeiter das Formular wieder auf das Angebot zurück, das er längst verlassen hat.
   * Findet sich die Kennung nicht in der Liste (etwa weil der Vorgang inzwischen weg ist),
   * passiert schlicht nichts: Der Reiter steht dann mit seiner Liste da, und das ist eine
   * brauchbare Auskunft — eine Fehlermeldung über einen Sprung wäre keine.
   *
   * `bearbeitenOeffnen` steht bewusst NICHT in den Abhängigkeiten: Die Funktion wird bei
   * jedem Zeichnen neu gebaut und setzt ausschliesslich Zustand — als Abhängigkeit liefe
   * dieser Effekt in jeder Runde erneut an.
   */
  const sprungErledigt = useRef(false);
  useEffect(() => {
    const id = p.oeffneAngebotId;
    if (id === undefined || id === null || angebote === null) return;
    if (sprungErledigt.current) return;
    const treffer = angebote.find((a) => a.id === id);
    if (treffer === undefined) return;
    sprungErledigt.current = true;
    bearbeitenOeffnen(treffer, jumpNote(p.jumpSource, "Aus der Angebotssuche geöffnet."));
  }, [p.oeffneAngebotId, angebote]);

  function bearbeitenOeffnen(a: Angebot, meldung: string | null = null): void {
    anlage.zuruecksetzen();
    setHinweis(meldung);
    setNeuOffen(false);
    setBearbeitetId(a.id);
  }

  /** Der Satz eines Vorgangs, den der Baustein weitergedreht hat (Version/Betrag aus dem
   *  Positions-Editor, frischer Kopf nach „Vorlage anwenden"), zurück in die Liste. */
  function fortschreiben(neu: Angebot): void {
    setAngebote((alle) => (alle === null
      ? alle : alle.map((a) => (a.id === neu.id ? neu : a))));
  }

  /**
   * Der Baustein hat den Vorgang durch eine ANDERE Kennung ersetzt — „Neue Version" oder
   * „Kopieren" (Review-Befund Fixrunde 1 zu Aufgabe 6). Anders als `fortschreiben` (das
   * über die ALTE Kennung sucht und hier ins Leere liefe: Die neue Kennung steht ja noch
   * gar nicht in `angebote`) lädt der Reiter seine LISTE neu — er zeigt ohnehin JEDEN
   * Vorgang des Kunden flach nebeneinander, ohne einen einzelnen „geöffneten" Vorgang wie
   * der Bereich.
   *
   * Was das Neuladen zeigt, hängt vom Weg ab (Fixrunde nach Gesamtreview, M2 —
   * `listOffers` liefert je Reihe NUR die jüngste Version, Aufgabe 3): Bei „Neue Version"
   * ERSETZT die neue Zeile die alte an derselben Stelle — die abgelöste (jetzt
   * „Veraltet") Version ist nicht mehr die jüngste ihrer Reihe und taucht deshalb NICHT
   * mehr auf. Bei „Kopieren" bleibt die Quelle unverändert stehen, und die Kopie kommt
   * als ZUSÄTZLICHE Zeile ihrer eigenen (neuen) Reihe hinzu.
   */
  function ersetzt(_neu: Angebot): void {
    void laden();
  }

  // Wie der Server (Festlegung 7): das fachliche Datum (createdOn) absteigend, dann createdAt — ein nacherfasster
  // Altbestand steht so bei seinem Jahr, nicht oben. Bei vollem Gleichstand bleibt die Serverfolge (stabile Sortierung;
  // die Kennung ordnet der Server binär, nicht als Text). ISO-Tage und -Zeitstempel sortieren lexikografisch.
  const sortiert = [...(angebote ?? [])].sort((a, b) =>
    b.createdOn.localeCompare(a.createdOn) || b.createdAt.localeCompare(a.createdAt));

  return (
    <div className="kang">
      <div className="kang__kopf">
        <h2 className="kang__titel">Angebote</h2>
        <KiKnopf art="primaer" className="kang__primaer" onClick={neuOeffnen}
          deaktiviert={formularOffen}>
          Neues Angebot
        </KiKnopf>
        <KiKnopf onClick={() => { setHinweis(null); setHistorischOffen(true); }} deaktiviert={formularOffen}>
          {HISTORICAL_TEXT.create.offer}
        </KiKnopf>
      </div>

      {companies.length === 0 && (
        <Hinweis art="info">
          Für diesen Kunden besteht noch keine Unternehmensbeziehung — ohne sie lässt sich kein
          Angebot anlegen.
        </Hinweis>
      )}

      {fehler !== null && <Hinweis art="fehler">{fehler}</Hinweis>}
      {hinweis !== null && <Hinweis art="info">{hinweis}</Hinweis>}

      {neuOffen && (
        <Modal titel="Neues Angebot" breite="weit" onAbbrechen={anlageAbbrechen}
          abbrechenGesperrt={anlage.speichert}>
          {/* Der Abschnitt behält seinen Namen: Er ist die Maske IM Panel, und Tests wie
              Screenreader finden sie weiter unter „Neues Angebot". */}
          <section className="kang__formular" aria-label="Neues Angebot">
            {/* DER ERSTE SCHRITT — Dirks Muss-Feature, wörtlich: „für ein neues Angebot wählt
                man einfach eine benannte Vorlage aus und füllt nur die Mengen ein". Deshalb
                steht die Wahl VOR den Kopffeldern und nicht als Zusatz darunter: Wer sie erst
                hinter Titel, Betrag und Laufzeit fände, tippte alles von Hand. „Ohne Vorlage"
                bleibt die Vorauswahl — ein leeres Angebot ist ein gültiger Anfang. */}
            <div className="kang__vorlagenwahl">
              {/* Die Kopffelder darunter (angebotFormularFelder, geteilt mit dem Bereich
                  Angebote) und die Anlage selbst (useAngebotAnlegen) ziehen erst mit
                  Aufgabe 4 auf Formular/useFormularZustand — hier nur die eigene Auswahl. */}
              <Auswahl id="kang-neu-vorlage" name="kang-neu-vorlage" beschriftung="Vorlage"
                wert={entwurf.vorlageId} gesperrt={anlage.speichert}
                onGeaendert={(d) => setEntwurf((v) => ({ ...v, vorlageId: d.wert }))}>
                <option value="">— ohne Vorlage —</option>
                {umfeld.vorlagen.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.name} ({v.itemCount} {v.itemCount === 1 ? "Position" : "Positionen"})
                  </option>
                ))}
              </Auswahl>
              <p className="kang__vorlagen-hinweis">
                Mit einer Vorlage stehen die Positionen sofort im neuen Angebot — dann nur noch
                die Mengen füllen.
              </p>
            </div>
            {/* Ohne Positionsblock: Am noch nicht angelegten Angebot gibt es keinen
                Vorgang, an dem Positionen hängen könnten — sie kommen mit der Vorlage
                oder gleich danach im Bearbeiten-Panel. Die Reihenfolge der übrigen Blöcke
                ist dieselbe wie dort (siehe angebotFormularFelder). */}
            {angebotFormularFelder({
              praefix: "kang-neu", mitUnternehmen: false, companies, umfeld, entwurf,
              setEntwurf, hatPositionen: false, feldFehler: anlage.feldFehler,
              fehlerJeFeld: anlage.fehlerJeFeld, formular: { laeuft: anlage.speichert },
              faltungen: anlage.faltungen,
              vorschauWerte: angebotVorschauWerte({
                entwurf, companies, kontakte: umfeld.kontakte, angebot: null,
                kundeName: p.kundeName,
              }),
            })}
          </section>

          <ModalFuss speichernText="Anlegen" speichernGesperrt={anlage.speichert}
            abbrechenGesperrt={anlage.speichert}
            onSpeichern={() => { void anlage.anlegen(); }} onAbbrechen={anlageAbbrechen} />
        </Modal>
      )}

      {historischOffen && (
        <HistoricalRecordPanel client={client} kundeId={kundeId} art="offer" vorhanden={null} firmen={companies}
          umfeld={{ kontakte: umfeld.kontakte, benutzer: umfeld.benutzer, historischeAngebote: [] }}
          onGespeichert={() => { setHistorischOffen(false); void laden(); }}
          onAbbrechen={() => setHistorischOffen(false)} />
      )}

      {angebote === null && fehler === null && <p className="kang__hinweis">Lädt …</p>}

      {angebote !== null && sortiert.length === 0 && (
        <p className="kang__leer">Für diesen Kunden ist noch kein Angebot erfasst.</p>
      )}

      {sortiert.length > 0 && (
        <ul className="kang__liste">
          {sortiert.map((a) => (
            <li key={a.id} className="kang__eintrag">
              <AngebotDetail
                client={client} kundeId={kundeId} angebot={a} companies={companies}
                umfeld={umfeld} bearbeitung={bearbeitetId === a.id}
                gesperrt={formularOffen} kundeName={p.kundeName}
                onBearbeiten={() => bearbeitenOeffnen(a)}
                onSchliessen={schliessen}
                onNeuLaden={laden}
                onHinweis={setHinweis}
                onFortschreiben={fortschreiben}
                onErsetzt={ersetzt}
                onAuftragAusAngebot={onAuftragAusAngebot}
                onOrdnerOeffnen={p.onOrdnerOeffnen} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
